import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Award, AlertCircle, CheckCircle } from 'lucide-react';
import { apiClient } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface TenantSummary {
  tenantId: string;
  tenantSlug: string;
  assessmentCounts: {
    total: number;
    sent: number;
    in_progress: number;
    completed: number;
    overdue: number;
  };
  certificationCount: number;
}

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
  onClick?: () => void;
}) {
  return (
    <Card
      className={onClick ? 'cursor-pointer hover:shadow-md hover:border-accent-cyan/30 transition-all' : undefined}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-bg-surface-2 ${accent ?? ''}`}>
          <Icon className="h-5 w-5 text-text-secondary" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-text-primary">{value}</p>
          <p className="text-xs text-text-secondary">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function PortalTenantDashboardPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery({
    queryKey: ['portal-tenant-summary', tenantSlug],
    queryFn: async () => {
      const { data } = await apiClient.get(`/portal/tenants/${encodeURIComponent(tenantSlug!)}/summary`);
      return data.data as TenantSummary;
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary capitalize">{tenantSlug}</h1>
        <p className="text-sm text-text-secondary mt-1">Tenant overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total assessments"
          value={summary.assessmentCounts.total}
          icon={ClipboardList}
          onClick={summary.assessmentCounts.total > 0 ? () => navigate(`/portal/tenants/${tenantSlug}/assessments`) : undefined}
        />
        <StatCard
          label="Sent"
          value={summary.assessmentCounts.sent}
          icon={ClipboardList}
          onClick={summary.assessmentCounts.sent > 0 ? () => navigate(`/portal/tenants/${tenantSlug}/assessments`) : undefined}
        />
        <StatCard
          label="In progress"
          value={summary.assessmentCounts.in_progress}
          icon={ClipboardList}
          onClick={summary.assessmentCounts.in_progress > 0 ? () => navigate(`/portal/tenants/${tenantSlug}/assessments`) : undefined}
        />
        <StatCard
          label="Completed"
          value={summary.assessmentCounts.completed}
          icon={CheckCircle}
          accent="text-accent-cyan"
          onClick={summary.assessmentCounts.completed > 0 ? () => navigate(`/portal/tenants/${tenantSlug}/assessments`) : undefined}
        />
        <StatCard
          label="Overdue"
          value={summary.assessmentCounts.overdue}
          icon={AlertCircle}
          accent="text-accent-rose"
          onClick={summary.assessmentCounts.overdue > 0 ? () => navigate(`/portal/tenants/${tenantSlug}/assessments`) : undefined}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Certifications" value={summary.certificationCount} icon={Award} />
      </div>
    </div>
  );
}
